import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import {
  useGetSmsTemplatesQuery,
  useCreateSmsTemplateMutation,
  useUpdateSmsTemplateMutation,
  useDeleteSmsTemplateMutation,
} from "apis/smsTemplates";
import { FaPlus } from "react-icons/fa6";
import SmsTemplateEdit from "components/smsTemplates/Edit";
import SmsTemplateView from "components/smsTemplates/View";
import Table from "components/ui/Table";

const SmsTemplates = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetSmsTemplatesQuery();

  const {
    mutate: createSmsTemplate,
    isLoading: isCreateLoading,
  } = useCreateSmsTemplateMutation();

  const { mutate: updateSmsTemplate } = useUpdateSmsTemplateMutation();

  const {
    mutate: deleteSmsTemplate,
    isLoading: isDeleteLoading,
  } = useDeleteSmsTemplateMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Template Name",
        isFilter: true,
        minWidth: "200px",
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: (row) => (
          <span className="line-clamp-2" title={row.description}>
            {row.description}
          </span>
        ),
        isFilter: true,
        minWidth: "300px",
      },
      {
        accessorKey: "placeholders",
        header: "Placeholders",
        cell: (row) => (
          <div className="flex flex-wrap gap-1">
            {row.placeholders && row.placeholders.length > 0 ? (
              <>
                {row.placeholders.slice(0, 3).map((placeholder, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                  >
                    {placeholder}
                  </span>
                ))}
                {row.placeholders.length > 3 && (
                  <span className="px-2 py-1 bg-lightgray text-darkgray rounded-full text-xs font-medium">
                    +{row.placeholders.length - 3} more
                  </span>
                )}
              </>
            ) : (
              <span className="text-darkgray italic text-sm">None</span>
            )}
          </div>
        ),
        isFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Template"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Template"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    []
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading sms templates: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedTemplate = data?.find((template) => template._id === selectedId);

  const handleEditSubmit = (formData) => {
    updateSmsTemplate({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createSmsTemplate(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteSmsTemplate(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddSmsTemplateButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={16} className="mr-1" />
      Add SMS Template
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit SMS Template"
        className="max-w-3xl"
      >
        <SmsTemplateEdit
          initialData={selectedTemplate}
          onSubmit={handleEditSubmit}
          existingTemplates={data || []}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add SMS Template"
        className="max-w-3xl"
      >
        <SmsTemplateEdit
          initialData={{}}
          onSubmit={handleCreateSubmit}
          existingTemplates={data || []}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete SMS Template"
      >
        <div>
          Are you sure you want to delete this SMS template?
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
            >
              No
            </button>
            <button onClick={handleDelete} className="black-button" disabled={isDeleteLoading}>
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="SMS Template Details"
        className="max-w-3xl"
      >
        <SmsTemplateView template={selectedTemplate} />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="SMS Templates"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddSmsTemplateButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default SmsTemplates;